import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageCircle, MapPin, Image as ImageIcon, Sparkles, X, Film, Play } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { waLink } from "../constants/data";
import { Sparkle, Heart, Flower } from "../components/doodles/Doodles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLUG_MAP = {
  birthday: "combo-birthday",
  bridal: "combo-bridal",
  baby: "combo-baby",
};

const msgFor = (name) =>
  `Hi Naathi Charami! 👋 I'd love to enquire about your "${name}" combo (In Hyderabad). Please share details, dates, themes & pricing. Thank you!`;

export default function ComboDetail() {
  const { slug: shortSlug } = useParams();
  const fullSlug = SLUG_MAP[shortSlug];
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!fullSlug) { setLoading(false); return; }
    axios.get(`${API}/catalogues`)
      .then((r) => {
        const found = (r.data || []).find((c) => c.slug === fullSlug);
        setCombo(found || null);
      })
      .finally(() => setLoading(false));
  }, [fullSlug]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    if (lightbox !== null) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [lightbox]);

  if (loading) {
    return <div className="pt-32 pb-20 text-center font-hand text-[#5C5555]">Loading…</div>;
  }
  if (!fullSlug || !combo) {
    return (
      <div className="pt-32 pb-20 text-center" data-testid="combo-not-found">
        <p className="font-doodle text-3xl text-[#7A1F2B]">Combo not found</p>
        <Link to="/combos" className="btn-primary mt-4 inline-flex">Back to Combos</Link>
      </div>
    );
  }

  const images = (combo.images || []).filter(Boolean).slice(0, 10);
  const videos = (combo.videos || []).filter(Boolean).slice(0, 4);
  const wa = waLink(msgFor(combo.name));

  return (
    <div className="pt-28 pb-20 min-h-[80vh] relative overflow-hidden" data-testid={`combo-detail-${shortSlug}`}>
      <Helmet>
        <title>{combo.name} — Naathi Charami Hyderabad</title>
        <meta name="description" content={combo.description?.slice(0, 160) || `${combo.name} combo in Hyderabad — cake, table grazing, photographer, decor and return gifts.`}/>
      </Helmet>

      <div className="absolute inset-0 -z-10" style={{
        background: "radial-gradient(circle at 20% 20%, #FFE0B5 0%, transparent 45%), radial-gradient(circle at 80% 80%, #FFD4DC 0%, transparent 45%), linear-gradient(135deg, #FFF7E6 0%, #FFE7F1 100%)",
      }}/>

      <Sparkle color="#FFD93D" className="absolute top-32 left-12 w-6 animate-twinkle"/>
      <Heart color="#FF6B9D" className="absolute top-1/3 right-10 w-7 animate-bounce-doodle"/>
      <Flower color="#FFA94D" className="absolute -top-3 -left-3 w-20 rotate-12 opacity-80"/>
      <Flower color="#FF6B9D" className="absolute -bottom-3 -right-3 w-20 -rotate-12 opacity-80"/>

      <div className="container-x relative">
        <Link to="/combos" data-testid="combo-back-link" className="inline-flex items-center gap-1.5 font-hand text-sm text-[#7A1F2B] hover:underline mb-3">
          <ArrowLeft className="w-4 h-4"/> All combos
        </Link>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Image gallery (10 slots) */}
          <div className="lg:col-span-3" data-testid="combo-gallery">
            {images.length === 0 ? (
              <div className="aspect-[4/3] bg-[#FFE7F1] border-2 border-dashed border-[#7A1F2B]/40 rounded-3xl flex flex-col items-center justify-center gap-2 text-[#9B9494]">
                <ImageIcon className="w-10 h-10 opacity-50"/>
                <p className="font-hand text-sm">Images coming soon</p>
              </div>
            ) : (
              <>
                {/* Hero image */}
                <button
                  type="button"
                  onClick={() => setLightbox(0)}
                  data-testid="combo-hero-image"
                  className="block w-full aspect-[4/3] doodle-card overflow-hidden bg-[#FFE7F1]"
                  style={{ boxShadow: "5px 5px 0 #2D2424" }}
                >
                  <img src={images[0]} alt={combo.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
                </button>
                {/* Thumbnails */}
                {images.length > 1 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                    {images.slice(1).map((src, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setLightbox(i + 1)}
                        data-testid={`combo-thumb-${i + 1}`}
                        className="aspect-square overflow-hidden rounded-xl border-2 border-[#2D2424]/20 hover:border-[#7A1F2B]"
                      >
                        <img src={src} alt={`${combo.name} ${i + 2}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"/>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Videos — uploaded from past events */}
            {videos.length > 0 && (
              <div className="mt-6" data-testid="combo-videos">
                <p className="font-doodle text-base text-[#7A1F2B] flex items-center gap-1.5 mb-2">
                  <Film className="w-4 h-4"/> See it in action
                </p>
                <div className={`grid gap-2 ${videos.length === 1 ? "" : "sm:grid-cols-2"}`}>
                  {videos.map((src, i) => (
                    <video
                      key={i}
                      src={src}
                      controls
                      preload="metadata"
                      playsInline
                      data-testid={`combo-video-${i}`}
                      className="w-full aspect-video rounded-2xl border-2 border-[#2D2424] bg-black"
                      style={{ boxShadow: "4px 4px 0 #2D2424" }}
                    >
                      <track kind="captions"/>
                    </video>
                  ))}
                </div>
                <p className="font-hand text-[11px] text-[#5C5555] mt-1 inline-flex items-center gap-1">
                  <Play className="w-3 h-3"/> tap to play
                </p>
              </div>
            )}
          </div>

          {/* Info column */}
          <div className="lg:col-span-2 flex flex-col">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#FFD93D] text-[#7A1F2B] text-[10px] font-doodle uppercase tracking-wider rounded-full self-start border border-[#7A1F2B]">
              <MapPin className="w-3 h-3"/> Hyderabad only
            </span>
            <h1 className="font-doodle text-3xl sm:text-4xl text-[#7A1F2B] mt-2 leading-tight" data-testid="combo-name">{combo.name}</h1>

            {combo.description && (
              <p className="mt-3 font-hand text-base text-[#2D2424] leading-relaxed whitespace-pre-line" data-testid="combo-description">{combo.description}</p>
            )}

            {combo.product_info && (
              <div className="mt-4 doodle-card p-4 bg-white">
                <p className="text-[10px] font-doodle uppercase tracking-widest text-[#7A1F2B] flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3"/> What&apos;s included
                </p>
                <p className="font-hand text-sm text-[#2D2424] mt-1 whitespace-pre-line" data-testid="combo-product-info">{combo.product_info}</p>
              </div>
            )}

            {combo.important_notes && (
              <div className="mt-3 px-3 py-2 bg-[#FFF7DC] border-2 border-dashed border-[#7A1F2B]/40 rounded-xl">
                <p className="text-[10px] font-doodle uppercase tracking-widest text-[#7A1F2B]">Important</p>
                <p className="font-hand text-xs text-[#2D2424] mt-1 whitespace-pre-line">{combo.important_notes}</p>
              </div>
            )}

            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              data-testid="combo-whatsapp-btn"
              className="btn-whatsapp mt-5"
            >
              <MessageCircle className="w-4 h-4"/> Enquire on WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && images[lightbox] && (
        <div
          className="fixed inset-0 z-[100] bg-[#2D2424]/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
          data-testid="combo-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white border-2 border-[#2D2424] flex items-center justify-center"
            style={{ boxShadow: "3px 3px 0 #2D2424" }}
            aria-label="close"
          >
            <X className="w-5 h-5"/>
          </button>
          <img src={images[lightbox]} alt="" className="max-w-full max-h-[88vh] object-contain rounded-2xl"/>
        </div>
      )}
    </div>
  );
}
